import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@zg-rentals/react-query5';
import { formatDistanceToNow } from 'date-fns';

import { Box, IconButton, Menu, Paragraph, Table } from '@zillow/constellation-10';
import { IconMoreFilled } from '@zillow/constellation-icons';

import { RentalManagerUserMaintenanceTaskWebApiHandler_list, useRmwPaths } from '@zg-rentals/rental-manager-env';

import { TASKS_TABLE, USE_TASKS_LIST } from '../constants/TaskConstants';
import { DeleteTaskModal } from './DeleteTaskModal';
import { StatusTag } from './StatusTag';

import { overflowParagraphStyles } from '../styles';
import { TasksTableSkeleton } from './TasksTableSkeleton';

import type { RentalManagerUserMaintenanceTaskDto } from '@zg-rentals/rental-manager-env';

const hiddenBelow = {
  sm: { display: { base: 'none', sm: 'table-cell' } },
  lg: { display: { base: 'none', lg: 'table-cell' } },
  xl: { display: { base: 'none', xl: 'table-cell' } },
  xxl: { display: { base: 'none', xxl: 'table-cell' } },
} as const;

type TaskToDisplay = Pick<
  RentalManagerUserMaintenanceTaskDto,
  'maintenanceTaskId' | 'name' | 'status' | 'updatedTimestamp' | 'cost'
> & {
  fullAddress?: string;
};

type Props = {
  /**
   * The formatted complete address of the property from zrmw FE since
   * `UserPropertySummaryMaintenanceTaskCardWebDto` does not have the fullAddress
   * unlike `RentalManagerUserMaintenanceTaskDto`.
   */
  formattedCompleteAddress?: string;
  listingAlias?: string;
  showAddressColumn?: boolean;
  fromExternalApp?: boolean;
  tasksToDisplay?: Array<TaskToDisplay>;
  onRowClick?: (taskId: string) => void;
  isLoading?: boolean;
};

export const TasksTable = ({
  formattedCompleteAddress,
  listingAlias,
  showAddressColumn = true,
  fromExternalApp = false,
  tasksToDisplay,
  onRowClick,
  isLoading = false,
}: Props) => {
  const history = useHistory();
  const paths = useRmwPaths();

  const tableRef = useRef<HTMLTableElement>(null);

  const [maintenanceTaskIdToDelete, setMaintenanceTaskIdToDelete] = useState('');
  const [idOfMenuPopper, setIdOfMenuPopper] = useState('');

  const { data, isInitialLoading } = useQuery({
    queryKey: [USE_TASKS_LIST],
    queryFn: () =>
      RentalManagerUserMaintenanceTaskWebApiHandler_list({
        includeInProgress: true,
      }),
    staleTime: 1000 * 3,
    enabled: tasksToDisplay && tasksToDisplay.length === 0,
  });

  const isDataLoading = isLoading || isInitialLoading;

  const handleTaskClick = (taskId: string) => {
    if (taskId) {
      if (fromExternalApp && onRowClick) {
        onRowClick(taskId);
      } else {
        history.push(`/rental-manager/maintenance-tasks/task?id=${taskId}`);
      }
    }
  };

  useEffect(() => {
    const closeOpenMenus = (e: MouseEvent) => {
      if (tableRef.current && idOfMenuPopper && !tableRef.current.contains(e.target as Node)) {
        setTimeout(() => {
          setIdOfMenuPopper('');
        }, 0);
      }
    };

    document.addEventListener('mouseup', closeOpenMenus);

    return () => document.removeEventListener('mouseup', closeOpenMenus);
  }, [idOfMenuPopper]);

  const tasksToRender = tasksToDisplay || data?.tasks;

  return (
    <>
      <Table.Root
        appearance="horizontal"
        aria-label="Tasks"
        size="md"
        ref={tableRef}
        css={{ whiteSpace: 'nowrap', '& td': { verticalAlign: 'middle' } }}
      >
        {isDataLoading ? (
          <TasksTableSkeleton />
        ) : (
          <>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                {showAddressColumn && <Table.HeaderCell css={hiddenBelow.lg}>Property address</Table.HeaderCell>}
                <Table.HeaderCell css={hiddenBelow.sm}>Status</Table.HeaderCell>
                <Table.HeaderCell css={hiddenBelow.xl}>Last updated</Table.HeaderCell>
                <Table.HeaderCell css={{ ...hiddenBelow.xxl, textAlign: 'right' }}>Cost</Table.HeaderCell>
                <Table.HeaderCell css={{ width: '0' }} aria-label="Actions">
                  {' '}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {tasksToRender?.map((task, idx) => {
                return (
                  <Table.Row
                    css={{ cursor: 'pointer' }}
                    key={`${task.maintenanceTaskId}-${idx}`}
                    onClick={() => handleTaskClick(task.maintenanceTaskId)}
                    interactive={true}
                  >
                    <Table.Cell>
                      <Paragraph textStyle="body-sm" css={overflowParagraphStyles}>
                        {task.name}
                      </Paragraph>

                      {showAddressColumn && (
                        <Paragraph
                          textStyle="fineprint"
                          css={{ ...overflowParagraphStyles, display: { base: 'block', lg: 'none' } }}
                          color="text.subtle"
                        >
                          {formattedCompleteAddress || task?.fullAddress}
                        </Paragraph>
                      )}
                    </Table.Cell>
                    {showAddressColumn && (
                      <Table.Cell css={hiddenBelow.lg}>
                        <Paragraph textStyle="body-sm" css={overflowParagraphStyles}>
                          {formattedCompleteAddress || task?.fullAddress}
                        </Paragraph>
                      </Table.Cell>
                    )}
                    <Table.Cell css={hiddenBelow.sm}>
                      <Box css={{ display: 'flex', alignItems: 'center' }}>
                        <StatusTag status={task.status} isLoading={isDataLoading} />
                      </Box>
                    </Table.Cell>
                    <Table.Cell css={hiddenBelow.xl}>
                      {formatDistanceToNow(new Date(Number(task?.updatedTimestamp)), { addSuffix: true })}
                    </Table.Cell>
                    <Table.Cell css={{ ...hiddenBelow.xxl, textAlign: 'right' }}>
                      {task.cost
                        ? `$${task.cost % 100 === 0 ? (task.cost / 100).toLocaleString() : (task.cost / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '-'}
                    </Table.Cell>

                    <Table.Cell css={{ width: '0' }}>
                      <Box
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                        }}
                      >
                        <Menu
                          open={idOfMenuPopper === task.maintenanceTaskId}
                          onOpenChange={(open) => open && setIdOfMenuPopper(task.maintenanceTaskId)}
                          trigger={
                            <IconButton
                              icon={<IconMoreFilled />}
                              title="Task options"
                              size="sm"
                              emphasis="bare"
                              tone="brand"
                            />
                          }
                          content={
                            <>
                              {fromExternalApp ? (
                                <Menu.Item asChild>
                                  <a href={`${paths.maintenanceTrackerPage}/task?id=${task.maintenanceTaskId}`}>
                                    Edit task
                                  </a>
                                </Menu.Item>
                              ) : (
                                <Menu.Item
                                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    handleTaskClick(task.maintenanceTaskId);
                                  }}
                                >
                                  Edit task
                                </Menu.Item>
                              )}
                              <Menu.Item
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  e.stopPropagation();

                                  setMaintenanceTaskIdToDelete(task.maintenanceTaskId);
                                }}
                              >
                                Delete task
                              </Menu.Item>
                            </>
                          }
                        />
                      </Box>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </>
        )}
      </Table.Root>
      {maintenanceTaskIdToDelete && (
        <DeleteTaskModal
          listingAlias={listingAlias}
          fromExternalApp={fromExternalApp}
          clickOriginationPoint={TASKS_TABLE}
          maintenanceTaskId={maintenanceTaskIdToDelete}
          onClose={() => {
            setMaintenanceTaskIdToDelete('');
          }}
        />
      )}
    </>
  );
};
